/**
 * Web Push opt-in helper.
 *
 * Every entry point guards on isSupported(). On a non-secure origin
 * (http://rcms.test), navigator.serviceWorker and window.PushManager are
 * undefined — this must degrade quietly, not throw.
 */

const SW_PATH = '/sw.js'

// How many times this browser profile has been asked. Per profile, not per
// user: the permission it guards is itself per-origin, not per-account.
const AUTO_PROMPT_KEY = 'rcms_push_auto_prompted'

// Chrome permanently blocks an origin on the third dismissal. Two asks gives a
// user who closed the first prompt — by far the most common outcome, and the
// one that leaves permission on 'default' with nothing on screen to fix it —
// a second chance, while keeping one strike in reserve.
const MAX_AUTO_PROMPTS = 2

export function isSupported() {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window &&
        !!window.vapidPublicKey
    )
}

export function permission() {
    return isSupported() ? Notification.permission : 'unsupported'
}

/**
 * applicationServerKey must be a Uint8Array — passing the base64 string
 * fails with an opaque error. This is the standard conversion.
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const raw = window.atob(base64)
    const output = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; ++i) {
        output[i] = raw.charCodeAt(i)
    }
    return output
}

async function register() {
    return navigator.serviceWorker.register(SW_PATH)
}

/**
 * Returns { ok, status } rather than a bare boolean: sync() has to tell a 403
 * (this user may not hold Notifications, and the stored row still points at
 * whoever subscribed before them) apart from every other failure.
 *
 * skipGlobalError opts out of bootstrap.js's axios interceptor. Push is a
 * background concern; a routine 403 on page load must not raise the global
 * "Forbidden" snackbar. There is no opt-in UI to report a failure through, so
 * autoPrompt() warns to the console instead.
 */
async function post(url, body) {
    try {
        await window.axios.post(url, body, { skipGlobalError: true })
        return { ok: true, status: 200 }
    } catch (e) {
        const status = e.response ? e.response.status : 0

        // 403 means the user lacks the Notifications permission. That is a
        // legitimate state, not an error to surface. Anything else (network
        // error, 500, CSRF mismatch, timeout) still must not throw, but
        // should not be indistinguishable from a routine permission denial.
        if (status !== 403) {
            console.warn('Push subscription request failed', url, e)
        }

        return { ok: false, status }
    }
}

/**
 * Creates the browser-level subscription and registers it server-side.
 *
 * Shared by enable() and sync() so the two cannot drift. Calling
 * pushManager.subscribe() raises no prompt when Notification.permission is
 * already 'granted', which is what lets sync() repair a torn-down
 * subscription silently and without a user gesture.
 */
async function subscribeAndPost(registration) {
    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(window.vapidPublicKey)
    })

    const result = await post('/push-subscribe', subscription.toJSON())

    return { ok: result.ok, status: result.status, subscription }
}

/**
 * Raises the browser permission prompt without waiting for a click, at most
 * MAX_AUTO_PROMPTS times per browser profile, then subscribes if granted.
 *
 * The cap is the whole design. Prompting on every page load runs an origin
 * into Chrome's three-dismissal permanent block; prompting exactly once left
 * anyone who closed the first dialog on permission 'default' with no dialog
 * coming and no control on screen to ask for one. Two asks covers the common
 * accidental dismissal and still keeps a strike in reserve.
 *
 * Two things this cannot do, both enforced by the browser rather than by us:
 *
 * - Once permission is 'denied', requestPermission() resolves to 'denied'
 *   immediately and shows nothing. No code can re-prompt; only the user can,
 *   from the site settings behind the address bar.
 * - Firefox has required a user gesture since FF72, so an automatic prompt
 *   there is auto-denied. Chrome and Edge, which is what RCMS runs on, do
 *   allow it.
 *
 * On a managed workstation, none of this needs to happen: Chrome's
 * NotificationsAllowedForUrls policy grants the permission outright, this
 * function returns early because permission is no longer 'default', and
 * sync() subscribes silently.
 */
export async function autoPrompt() {
    if (!isSupported() || Notification.permission !== 'default') {
        return false
    }

    try {
        const asked = Number(window.localStorage.getItem(AUTO_PROMPT_KEY)) || 0

        if (asked >= MAX_AUTO_PROMPTS) {
            return false
        }

        // Counted before the prompt opens, not after it is answered. A reload
        // while the dialog is still up would otherwise cost a second ask
        // without the user having done anything.
        window.localStorage.setItem(AUTO_PROMPT_KEY, String(asked + 1))
    } catch (e) {
        // Private mode or blocked site data: with nowhere to record the count,
        // asking at all risks running through Chrome's budget unnoticed.
        return false
    }

    const enabled = await enable()

    // With no banner and no Enable button, a failure here is otherwise
    // completely silent, and the attempts are capped. The console is the only
    // place left to say so.
    if (!enabled && Notification.permission === 'granted') {
        console.warn('Push permission was granted but the subscription failed.')
    }

    return enabled
}

/**
 * Raises the permission prompt and subscribes. Reached from the bell's Enable
 * button and from autoPrompt() above.
 */
export async function enable() {
    if (!isSupported()) {
        return false
    }

    try {
        const result = await Notification.requestPermission()
        if (result !== 'granted') {
            return false
        }

        const registration = await register()

        return (await subscribeAndPost(registration)).ok
    } catch (e) {
        // A stale applicationServerKey after VAPID key rotation, or a
        // browser policy block, would otherwise reject here. Task 8 awaits
        // this with no catch of its own, so this must not throw.
        return false
    }
}

/**
 * Drops this device's subscription. Called on logout so a shared cashier
 * workstation does not keep receiving the previous user's notifications.
 *
 * getRegistration(), not register(): logout must not install the service
 * worker on a browser that never opted in, and an install is one more thing
 * that can stall while the user is waiting to be logged out.
 */
export async function disable() {
    if (!isSupported()) {
        return false
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration()
        if (!registration) {
            return false
        }

        const subscription = await registration.pushManager.getSubscription()
        if (!subscription) {
            return false
        }

        const endpoint = subscription.endpoint
        await subscription.unsubscribe()

        return (await post('/push-unsubscribe', { endpoint })).ok
    } catch (e) {
        return false
    }
}

/**
 * Re-posts the current subscription on every page load.
 *
 * A browser can rotate a subscription without telling the page
 * (pushsubscriptionchange exists but support is unreliable), and the row may
 * have been pruned server-side. /push-subscribe is an upsert keyed on
 * endpoint, so this is safe to repeat and also reassigns the row to whoever
 * is currently logged in.
 *
 * When permission is 'granted' but no subscription exists, this re-creates
 * it. Without that, disable() on the first logout would end push permanently
 * for that browser profile: permission never returns to 'default', so the
 * opt-in banner would never be offered again either.
 */
export async function sync() {
    if (!isSupported() || Notification.permission !== 'granted') {
        return false
    }

    try {
        const registration = await register()
        const existing = await registration.pushManager.getSubscription()

        const result = existing
            ? { ...(await post('/push-subscribe', existing.toJSON())), subscription: existing }
            : await subscribeAndPost(registration)

        // A 403 here means the logged-in user does not hold Notifications,
        // while the stored row still belongs to the previous user of this
        // workstation — so their notification titles would keep appearing on
        // this screen. Tear the endpoint down locally; the server prunes the
        // row on its next 410. Deliberately not done in enable(): there the
        // POST wrote nothing, so no row is left pointing at anyone, and
        // unsubscribing a subscription the user just asked for would churn.
        if (!result.ok && result.status === 403) {
            await result.subscription.unsubscribe()
        }

        return result.ok
    } catch (e) {
        return false
    }
}
